import { Alert, Box, Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { loginPage } from '@/constants/loginPage';
import type { AuthAccount } from '@/types/auth';
import { AccountSelectionPanel } from './AccountSelectionPanel';
import { AzureLoginButton } from './AzureLoginButton';
import { EnterpriseBadge } from './EnterpriseBadge';
import { LoginLogo } from './LoginLogo';
import { SecureGatewayDivider } from './SecureGatewayDivider';
import { SecurityFooter } from './SecurityFooter';
import { WelcomeSection } from './WelcomeSection';

interface LoginCardProps {
  availableAccounts: AuthAccount[];
  requiresAccountSelection: boolean;
  isLoading: boolean;
  isSelectingAccount: boolean;
  errorMessage?: string;
  onLogin: () => void;
  onSelectAccount: (accountId: string) => void;
}

export function LoginCard({
  availableAccounts,
  requiresAccountSelection,
  isLoading,
  isSelectingAccount,
  errorMessage,
  onLogin,
  onSelectAccount,
}: LoginCardProps) {
  const theme = useTheme();
  const isLoginDisabled = isSelectingAccount || requiresAccountSelection;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        animation: 'loginCardFadeIn 420ms ease forwards',
        '@keyframes loginCardFadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Paper
        component="section"
        aria-labelledby="login-page-title"
        elevation={0}
        sx={{
          width: `min(${loginPage.cardWidth}px, 100%)`,
          borderRadius: loginPage.cardBorderRadius,
          px: loginPage.cardPaddingX,
          py: loginPage.cardPaddingY,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 16px 40px rgba(0, 0, 0, 0.45)'
              : loginPage.cardShadow,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={3.5} alignItems="center">
          <LoginLogo />
          <WelcomeSection />

          {errorMessage ? (
            <Alert severity="error" sx={{ width: '100%' }} role="alert">
              {errorMessage}
            </Alert>
          ) : null}

          {requiresAccountSelection ? (
            <AccountSelectionPanel
              availableAccounts={availableAccounts}
              isSelectingAccount={isSelectingAccount}
              onSelectAccount={onSelectAccount}
            />
          ) : null}

          <Stack spacing={2.5} alignItems="center" sx={{ width: '100%' }}>
            <AzureLoginButton
              isLoading={isLoading}
              disabled={isLoginDisabled}
              onLogin={onLogin}
            />
            <SecureGatewayDivider />
            <EnterpriseBadge />
            <SecurityFooter />
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
